
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { BotApiTokenScopes } from '@shared/index';
import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ApiTokenInFrontend, BotApiTokenService } from 'src/app/shared/services/bot-api-token.service';
import { ManualLinkComponent } from "../../../../../../shared/components/rag-admin/manual-link/manual-link.component";

type BotApiTokenScopeDescription = {
  scope: BotApiTokenScopes,
  desciption: string
}
type Expiration = {
  duration: Duration,
  description: string
}

@Component({
  imports: [
    FormsModule,
    TooltipModule,
    ManualLinkComponent,
    MessageModule,
    InputTextModule,
    CardModule,
    CheckboxModule,
    SelectModule,
    ButtonModule,
    TableModule
  ],
  templateUrl: './externalapi-tab.component.html',
  styleUrl: './externalapi-tab.component.scss'
})
export class ExternalapiTabComponent {

  public readonly botApiTokenScopes: BotApiTokenScopeDescription[] = [
    { scope: "userlist:read", desciption: $localize`:@@userlist-read-description:Abfragen der Nutzerfreigaben` },
    { scope: "userlist:write", desciption: $localize`:@@userlist-write-description:Ändern der Nutzerfreigaben` },
    { scope: "userlistorg:read", desciption: $localize`:@@userlistorg-read-description:Abfragen der Nutzerfreigaben der gesamten Organisation` },
    { scope: "supervisor:read", desciption: $localize`:@@supervisor-read-description:Abfragen der Supervisor-Chats` },
    { scope: "keyval:read", desciption: $localize`:@@keyval-read-description:Bot KeyVal-Einträge lesen` },
    { scope: "keyval:write", desciption: $localize`:@@keyval-write-description:Bot KeyVal-Einträge schreiben und löschen` },
    { scope: "bot:read", desciption: $localize`:@@bot-read-description:Bot-Konfiguration abfragen` },
    { scope: "bot:write", desciption: $localize`:@@bot-write-description:Bot-Konfiguration bearbeiten, duplizieren und den Bot löschen` },
    { scope: "skills:read", desciption: $localize`:@@skills-read-description:Skills (Wenn-Dann-Regeln, Online-Quellen, Webhooks) abfragen` },
    { scope: "skills:write", desciption: $localize`:@@skills-write-description:Skills (Wenn-Dann-Regeln, Online-Quellen, Webhooks) erstellen, bearbeiten und löschen` },
    { scope: "stages:read", desciption: $localize`:@@stages-read-description:Stages (Gesprächsphasen) abfragen` },
    { scope: "stages:write", desciption: $localize`:@@stages-write-description:Stages (Gesprächsphasen) erstellen, bearbeiten und löschen` },
    { scope: "fusion:read", desciption: $localize`:@@fusion-read-description:Fusion-Views abfragen` },
    { scope: "fusion:write", desciption: $localize`:@@fusion-write-description:Fusion-Views erstellen, bearbeiten und löschen` },
    { scope: "embedding:read", desciption: $localize`:@@embedding-read-description:Website-Crawls abfragen` },
    { scope: "embedding:write", desciption: $localize`:@@embedding-write-description:Website-Crawls starten, bearbeiten und löschen` },
    { scope: "changelog:read", desciption: $localize`:@@changelog-read-description:Änderungsverlauf des Bots abfragen` },
    { scope: "stats:read", desciption: $localize`:@@stats-read-description:Nutzungsstatistiken und Feedback abfragen` }
  ];

  public readonly expirations: Expiration[] = [
    { duration: dayjs.duration({ days: 7 }), description: "7 " + $localize`:@@days:Tage` },
    { duration: dayjs.duration({ days: 30 }), description: "30 " + $localize`:@@days:Tage` },
    { duration: dayjs.duration({ days: 60 }), description: "60 " + $localize`:@@days:Tage` },
    { duration: dayjs.duration({ days: 90 }), description: "90 " + $localize`:@@days:Tage` },
    { duration: dayjs.duration({ days: 365 }), description: "365 " + $localize`:@@days:Tage` },
    { duration: dayjs.duration({ years: 5 }), description: "5 " + $localize`:@@years:Jahre` },
    { duration: dayjs.duration({ years: 100 }), description: $localize`:@@years-never:Verfällt nie` }
  ];

  public newTokenName: string = "";
  public newTokenScopeSet: Set<BotApiTokenScopes> = new Set<BotApiTokenScopes>();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  public newTokenExpiration: Duration = this.expirations[0]!.duration;

  public accessToken?: string;

  constructor(public apiTokenService: BotApiTokenService, private toastr: ToastrService) { }

  public toggleNewScope(scope: BotApiTokenScopes) {
    if (this.newTokenScopeSet.has(scope)) {
      this.newTokenScopeSet.delete(scope);
    } else {
      this.newTokenScopeSet.add(scope);
    }
  }

  public formValidForGeneration(): boolean {
    if (this.newTokenName.trim().length === 0 || this.newTokenScopeSet.size === 0) {
      return false;
    }
    return true;
  }

  public async generateToken() {
    const res = await this.apiTokenService.createApiToken(this.newTokenName.trim(), Array.from(this.newTokenScopeSet).sort(), this.newTokenExpiration.asSeconds());
    this.accessToken = res.token;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.newTokenExpiration = this.expirations[0]!.duration;
    this.newTokenName = "";
    this.newTokenScopeSet.clear();
  }

  public getExpirationDay(expiration: Duration) {
    return dayjs().add(expiration).format("dddd DD.MM.YYYY");
  }

  public getExpiraitionFromToken(token: ApiTokenInFrontend) {
    if (!token.exp || !token.iat) return "Error";
    if (dayjs(token.exp * 1000).diff(dayjs(), "years") > 30) return $localize`:@@token-never-expires:Verfällt nie`;
    return $localize`:@@token-expires-at:Verfällt am` + " " + dayjs(token.exp * 1000).format("dddd DD.MM.YYYY");
  }

  public getLastUsed(token: ApiTokenInFrontend) {
    if (dayjs(token.lastUsed).unix() == 0) return $localize`:@@never-used:Wurde noch nie verwendet`;
    return $localize`:@@last-used:Zuletzt verwendet` + " " + dayjs(token.lastUsed).fromNow();
  }

  public async copyToken() {
    if (!this.accessToken) return;
    await navigator.clipboard.writeText(this.accessToken);
    this.toastr.success("GENERAL", $localize`:@@copied-success:Token in Zwischenablage kopiert!`);
  }

}
